/**
 * Aktuelle Rolle (Manager/Worker-Session) — kanonischer KV-Key + Legacy-Wiederherstellung.
 */

import { kvGet, kvSet, kvRemove, kvSetPersisted, kvRemovePersisted } from '../infrastructure/storage/kvAccess';
import { normalizeBusinessStorageId } from './sharedCore';
import { normalizeWorkerPermissions } from './workerPermissions';

export function roleStorageKey(businessId) {
  if (businessId == null || businessId === '') return 'currentRole';
  return `currentRole_${normalizeBusinessStorageId(businessId)}`;
}

export function legacyRoleStorageKeys(businessId) {
  if (businessId == null || businessId === '') return ['currentRole'];

  const raw = String(businessId);
  const normalized = normalizeBusinessStorageId(businessId);
  const keys = [];
  const add = (k) => {
    if (k && !keys.includes(k)) keys.push(k);
  };

  add(roleStorageKey(businessId));
  add(`currentRole_${raw}`);
  if (normalized !== raw) add(`currentRole_${normalized}`);
  add('currentRole');

  return keys;
}

export function parseRoleKvValue(value) {
  if (value == null) return { role: null, corrupted: false };

  if (typeof value === 'string') {
    if (
      value === '[object Object]' ||
      value === '"[object Object]"' ||
      value.trim() === ''
    ) {
      return { role: null, corrupted: true };
    }
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { role: parsed, corrupted: false };
      }
      return { role: null, corrupted: true };
    } catch {
      return { role: null, corrupted: true };
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return { role: value, corrupted: false };
  }
  return { role: null, corrupted: true };
}

export function loadRoleForBusiness(businessId) {
  const keys = legacyRoleStorageKeys(businessId);
  const canonical = roleStorageKey(businessId);

  for (const key of keys) {
    const raw = kvGet(key);
    if (raw == null) continue;
    const { role, corrupted } = parseRoleKvValue(raw);
    if (corrupted || !role) continue;
    if (key !== canonical) {
      kvSet(canonical, role);
      console.info(`[roleStorage] Migrated role from ${key} → ${canonical}`);
    }
    return { role, sourceKey: key, migrated: key !== canonical };
  }

  return { role: null, sourceKey: null, migrated: false };
}

export function recoverRoleOnStartup() {
  try {
    const currentBusiness = kvGet('currentBusiness');
    let businessId = null;
    if (currentBusiness) {
      const business =
        typeof currentBusiness === 'string' ? JSON.parse(currentBusiness) : currentBusiness;
      if (business?.id) businessId = normalizeBusinessStorageId(business.id);
    }
    if (!businessId) return { role: null, sourceKey: null, migrated: false };
    return loadRoleForBusiness(businessId);
  } catch (e) {
    console.warn('[roleStorage] recoverRoleOnStartup failed:', e?.message || e);
    return { role: null, sourceKey: null, migrated: false };
  }
}

/**
 * Schreibt kanonischen Key und entfernt Legacy-Varianten (currentRole ohne Suffix, …).
 */
export async function consolidateLegacyRoleKeys(businessId, role) {
  const resolvedId = businessId == null || businessId === '' ? null : normalizeBusinessStorageId(businessId);
  const canonical = roleStorageKey(resolvedId);

  if (role != null) {
    await kvSetPersisted(canonical, role);
  }

  const removed = [];
  for (const legacyKey of legacyRoleStorageKeys(resolvedId)) {
    if (legacyKey === canonical) continue;
    kvRemove(legacyKey);
    await kvRemovePersisted(legacyKey);
    removed.push(legacyKey);
  }

  return { canonical, removed };
}

export function persistRoleForBusiness(businessId, role, options = {}) {
  const { allowRemove = false } = options;
  const key = roleStorageKey(businessId);

  if (role == null) {
    if (!allowRemove) {
      const existing = loadRoleForBusiness(businessId);
      if (existing.role) {
        return { skipped: true, reason: 'would_remove_existing_role', key };
      }
    }
    kvRemove(key);
    return { skipped: false, key, removed: true };
  }

  let toStore = role;
  if (role && role.type === 'worker') {
    toStore = {
      ...role,
      permissions: normalizeWorkerPermissions(role.permissions),
    };
  }

  kvSet(key, toStore);
  if (businessId != null && businessId !== '') {
    void consolidateLegacyRoleKeys(businessId, toStore);
  }
  return { skipped: false, key, removed: false };
}
